import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import StyledQR, { QR_STYLES } from '../components/StyledQR';
import { getHotels } from '../hotels';

const hotels = getHotels();

const BASE_URL = 'https://gapgo.redspeed.com.au';

/* ---------- Card design themes ---------- */
export const CARD_STYLES = [
  { id: 'white', name: 'Clean White', desc: 'Any printer', ink: 'low' },
  { id: 'dark', name: 'Bold Dark', desc: 'Good printer', ink: 'high' },
  { id: 'gradient', name: 'Gradient', desc: 'Good printer', ink: 'high' },
  { id: 'minimal', name: 'Minimal', desc: 'Any printer', ink: 'low' },
  { id: 'gold', name: 'Luxury Gold', desc: 'Good printer', ink: 'high' },
];

function getTheme(id) {
  switch (id) {
    case 'dark':
      return {
        dark: true,
        cardBg: '#0f0f0f',
        cardBorder: '1px solid #222',
        accentTop: 'linear-gradient(90deg, #E8001D, #FF3B53)',
        accentBottom: 'linear-gradient(90deg, #E8001D, #FF3B53)',
        propLabel: '#FF3B53',
        textPrimary: '#ffffff',
        textSecondary: '#888888',
        divider: '#222',
        roomBorder: '2px solid #FF3B53',
        roomLabel: '#FF3B53',
        roomNum: '#ffffff',
        hookBg: 'rgba(255,107,53,0.12)',
        hookColor: '#FF8A5B',
        hookBorder: '1px solid rgba(255,107,53,0.3)',
        hookTitle: '#ffffff',
        hookSub: '#888',
        scanCta: '#FF6B6B',
        scanSub: '#666',
        serviceBg: '#1a1a1a',
        serviceNameColor: '#fff',
        serviceDescColor: '#888',
        footerColor: '#555',
        qrTile: true,
      };
    case 'gradient':
      return {
        dark: true,
        cardBg: 'linear-gradient(140deg, #E8001D 0%, #7a1020 45%, #1a0508 100%)',
        cardBorder: 'none',
        accentTop: 'transparent',
        accentBottom: 'transparent',
        propLabel: 'rgba(255,255,255,0.7)',
        textPrimary: '#ffffff',
        textSecondary: 'rgba(255,255,255,0.7)',
        divider: 'rgba(255,255,255,0.15)',
        roomBorder: '2px solid rgba(255,255,255,0.6)',
        roomLabel: 'rgba(255,255,255,0.9)',
        roomNum: '#ffffff',
        hookBg: 'rgba(255,255,255,0.15)',
        hookColor: '#ffffff',
        hookBorder: '1px solid rgba(255,255,255,0.25)',
        hookTitle: '#ffffff',
        hookSub: 'rgba(255,255,255,0.7)',
        scanCta: '#ffffff',
        scanSub: 'rgba(255,255,255,0.6)',
        serviceBg: 'rgba(255,255,255,0.12)',
        serviceNameColor: '#fff',
        serviceDescColor: 'rgba(255,255,255,0.7)',
        footerColor: 'rgba(255,255,255,0.5)',
        qrTile: true,
      };
    case 'minimal':
      return {
        dark: false,
        cardBg: '#ffffff',
        cardBorder: '1px solid #eaeaea',
        accentTop: 'transparent',
        accentBottom: 'transparent',
        propLabel: '#E8001D',
        textPrimary: '#111111',
        textSecondary: '#999',
        divider: '#f0f0f0',
        roomBorder: '1.5px solid #111',
        roomLabel: '#888',
        roomNum: '#111',
        hookBg: 'transparent',
        hookColor: '#E8001D',
        hookBorder: 'none',
        hookTitle: '#111',
        hookSub: '#aaa',
        scanCta: '#111',
        scanSub: '#bbb',
        serviceBg: '#fafafa',
        serviceNameColor: '#111',
        serviceDescColor: '#999',
        footerColor: '#ccc',
        qrTile: false,
        minimal: true,
      };
    case 'gold':
      return {
        dark: true,
        cardBg: '#0a0a0a',
        cardBorder: '1px solid #2a2410',
        accentTop: 'linear-gradient(90deg, #BF953F, #FCF6BA, #BF953F)',
        accentBottom: 'linear-gradient(90deg, #BF953F, #FCF6BA, #BF953F)',
        propLabel: '#FFFFFF',
        textPrimary: '#F5F0E0',
        textSecondary: '#9a8f6a',
        divider: '#2a2410',
        roomBorder: '2px solid #FFFFFF',
        roomLabel: '#FFFFFF',
        roomNum: '#F5F0E0',
        hookBg: 'rgba(255,255,255,0.1)',
        hookColor: '#FFFFFF',
        hookBorder: '1px solid rgba(255,255,255,0.3)',
        hookTitle: '#F5F0E0',
        hookSub: '#9a8f6a',
        scanCta: '#FFFFFF',
        scanSub: '#6a6248',
        serviceBg: '#15130c',
        serviceNameColor: '#F5F0E0',
        serviceDescColor: '#9a8f6a',
        footerColor: '#6a6248',
        qrTile: true,
        gold: true,
      };
    case 'white':
    default:
      return {
        dark: false,
        cardBg: '#ffffff',
        cardBorder: '1px solid #e8e8e8',
        accentTop: 'linear-gradient(90deg, #E8001D, #FF3B53)',
        accentBottom: 'linear-gradient(90deg, #E8001D, #FF3B53)',
        propLabel: '#E8001D',
        textPrimary: '#111111',
        textSecondary: '#888',
        divider: '#f0f0f0',
        roomBorder: '2px solid #E8001D',
        roomLabel: '#E8001D',
        roomNum: '#111',
        hookBg: '#FFF3EF',
        hookColor: '#FF6B35',
        hookBorder: '1px solid #FF6B3530',
        hookTitle: '#111',
        hookSub: '#999',
        scanCta: '#E8001D',
        scanSub: '#bbb',
        serviceColorful: true,
        footerColor: '#ddd',
        qrTile: false,
      };
  }
}

/* ---------- Illustrations ---------- */
const BurgerSVG = () => (
  <svg width="64" height="64" viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="40" r="38" fill="#FFF3EF" />
    <ellipse cx="40" cy="52" rx="22" ry="5" fill="#D4522A" />
    <rect x="18" y="46" width="44" height="8" rx="2" fill="#E8A87C" />
    <rect x="20" y="38" width="40" height="8" fill="#E8C87C" />
    <rect x="20" y="32" width="40" height="8" fill="#8B1A1A" />
    <rect x="20" y="26" width="40" height="8" fill="#C4D98B" />
    <ellipse cx="40" cy="26" rx="22" ry="7" fill="#E8843A" />
    <ellipse cx="32" cy="22" rx="3" ry="2" fill="#fff" opacity="0.4" />
  </svg>
);
const CarSVG = () => (
  <svg width="64" height="64" viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="40" r="38" fill="#F0FBF4" />
    <rect x="12" y="38" width="56" height="18" rx="6" fill="#1DB954" />
    <path d="M20 38 L26 26 L54 26 L60 38Z" fill="#17A348" />
    <rect x="26" y="28" width="12" height="8" rx="2" fill="#AEE8C8" />
    <rect x="42" y="28" width="12" height="8" rx="2" fill="#AEE8C8" />
    <circle cx="24" cy="56" r="7" fill="#222" />
    <circle cx="24" cy="56" r="4" fill="#555" />
    <circle cx="56" cy="56" r="7" fill="#222" />
    <circle cx="56" cy="56" r="4" fill="#555" />
    <rect x="58" y="42" width="8" height="4" rx="2" fill="#FFE566" />
    <rect x="14" y="42" width="6" height="4" rx="2" fill="#FF6B6B" />
  </svg>
);
const CameraSVG = () => (
  <svg width="64" height="64" viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="40" r="38" fill="#F5F3FF" />
    <rect x="14" y="30" width="52" height="32" rx="6" fill="#7C3AED" />
    <path d="M28 30 L32 22 L48 22 L52 30Z" fill="#6D28D9" />
    <circle cx="40" cy="46" r="10" fill="#fff" opacity="0.2" />
    <circle cx="40" cy="46" r="5" fill="#fff" opacity="0.9" />
    <circle cx="40" cy="46" r="3" fill="#7C3AED" />
    <rect x="56" y="34" width="6" height="4" rx="2" fill="#DDD6FE" />
    <circle cx="22" cy="36" r="2" fill="#DDD6FE" />
  </svg>
);

const SERVICES = [
  { color: '#FF6B35', bg: '#FFF3EF', tag: 'ORDER FOOD', name: 'Room Dining', desc: 'Hot meals to your door', Svg: BurgerSVG },
  { color: '#1DB954', bg: '#F0FBF4', tag: 'BOOK A RIDE', name: 'Pick & Drop', desc: 'Safe rides anytime', Svg: CarSVG },
  { color: '#7C3AED', bg: '#F5F3FF', tag: 'BOOK SESSION', name: 'Photography', desc: 'Capture your stay', Svg: CameraSVG },
];

/* QR wrapped in a white tile when on dark backgrounds */
function QRBlock({ url, qrStyle, theme, size }) {
  const inner = <StyledQR value={url} size={size} styleId={qrStyle} />;
  if (theme.qrTile) {
    return <div style={{ background: '#fff', padding: 12, borderRadius: 14, display: 'inline-block' }}>{inner}</div>;
  }
  return <div style={{ background: '#fff', padding: 10, border: '1px solid #ececec', borderRadius: 14, display: 'inline-block' }}>{inner}</div>;
}

/* ---------- Door Card ---------- */
function DoorCard({ hotel, room, url, qrStyle, cardStyle }) {
  const t = getTheme(cardStyle);
  return (
    <div style={{ width: 460, background: t.cardBg, borderRadius: 16, overflow: 'hidden', border: t.cardBorder, fontFamily: '-apple-system, Arial, sans-serif' }}>
      {!t.minimal && t.accentTop !== 'transparent' && <div style={{ height: 10, background: t.accentTop }} />}
      {t.minimal && <div style={{ height: 4, background: '#E8001D' }} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px 12px' }}>
        <div>
          <p style={{ fontSize: 9, letterSpacing: 2, color: t.propLabel, fontWeight: 800, margin: '0 0 4px', textTransform: 'uppercase' }}>Property</p>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: t.textPrimary, margin: 0, letterSpacing: -0.5 }}>{hotel.name}</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: t.roomBorder, borderRadius: 10, padding: '8px 14px', gap: 2 }}>
          <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 2, color: t.roomLabel }}>ROOM</span>
          <span style={{ fontSize: 26, fontWeight: 900, color: t.roomNum, lineHeight: 1 }}>{room}</span>
        </div>
      </div>

      <div style={{ height: 1, background: t.divider, margin: '0 24px' }} />

      <div style={{ textAlign: 'center', padding: t.minimal ? '20px 24px 10px' : '16px 24px 8px' }}>
        {!t.minimal && (
          <div style={{ display: 'inline-block', background: t.hookBg, color: t.hookColor, fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '4px 14px', marginBottom: 10, border: t.hookBorder }}>
            ✨ Exclusive Guest Services
          </div>
        )}
        <p style={{ fontSize: t.minimal ? 13 : 15, fontWeight: 800, color: t.hookTitle, margin: '0 0 4px' }}>{t.minimal ? 'Scan for guest services' : 'Everything you need, one scan away'}</p>
        {!t.minimal && <p style={{ fontSize: 11, color: t.hookSub, margin: 0 }}>Food delivery · Safe rides · Photography</p>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: t.minimal ? '14px 24px 24px' : '8px 24px' }}>
        <QRBlock url={url} qrStyle={qrStyle} theme={t} size={t.minimal ? 230 : 200} />
        <p style={{ fontSize: 16, fontWeight: 800, color: t.scanCta, margin: 0 }}>Scan to Order</p>
        <p style={{ fontSize: 10, color: t.scanSub, margin: 0 }}>No app download needed · Works on any phone</p>
      </div>

      {!t.minimal && (
        <div style={{ display: 'flex', gap: 8, padding: '12px 24px' }}>
          {SERVICES.map((s, i) => {
            const bg = t.serviceColorful ? s.bg : t.serviceBg;
            return (
              <div key={i} style={{ flex: 1, background: bg, borderRadius: 10, padding: '12px 8px', borderTop: '3px solid ' + s.color, textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><s.Svg /></div>
                <span style={{ fontSize: 7, fontWeight: 800, color: s.color, background: s.color + '22', borderRadius: 20, padding: '2px 8px', display: 'inline-block', marginBottom: 6, letterSpacing: 0.3 }}>{s.tag}</span>
                <p style={{ fontSize: 10, fontWeight: 800, color: t.serviceColorful ? '#111' : t.serviceNameColor, margin: '0 0 3px' }}>{s.name}</p>
                <p style={{ fontSize: 8, color: t.serviceColorful ? '#888' : t.serviceDescColor, margin: 0, lineHeight: 1.4 }}>{s.desc}</p>
              </div>
            );
          })}
        </div>
      )}

      {t.minimal && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 18, padding: '0 24px 18px' }}>
          {SERVICES.map((s, i) => (
            <span key={i} style={{ fontSize: 10, fontWeight: 700, color: s.color }}>{s.name}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 24px', borderTop: '1px solid ' + t.divider }}>
        <p style={{ fontSize: 8, color: t.footerColor, margin: 0 }}>gapgo.redspeed.com.au</p>
        <p style={{ fontSize: 8, color: t.footerColor, margin: 0 }}>Powered by RedSpeed</p>
      </div>

      {!t.minimal && t.accentBottom !== 'transparent' && <div style={{ height: 5, background: t.accentBottom }} />}
    </div>
  );
}

/* ---------- Bedside Card ---------- */
function BedsideCard({ hotel, room, url, qrStyle, cardStyle }) {
  const t = getTheme(cardStyle);
  return (
    <div style={{ width: 500, background: t.cardBg, borderRadius: 12, overflow: 'hidden', border: t.cardBorder, display: 'flex', fontFamily: '-apple-system, Arial, sans-serif', minHeight: 190 }}>
      <div style={{ width: 7, background: t.accentTop === 'transparent' ? '#E8001D' : t.accentTop, flexShrink: 0 }} />
      <div style={{ flex: 1, padding: '18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ fontSize: 7, letterSpacing: 2, color: t.propLabel, fontWeight: 800, margin: '0 0 3px' }}>PROPERTY</p>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: t.textPrimary, margin: 0 }}>{hotel.name}</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: t.roomBorder, borderRadius: 8, padding: '5px 10px', gap: 1, flexShrink: 0 }}>
            <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: 1.5, color: t.roomLabel }}>ROOM</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: t.roomNum, lineHeight: 1 }}>{room}</span>
          </div>
        </div>
        {!t.minimal && (
          <div style={{ display: 'inline-block', background: t.hookBg, color: t.hookColor, fontSize: 9, fontWeight: 700, borderRadius: 20, padding: '3px 10px', border: t.hookBorder, alignSelf: 'flex-start' }}>
            ✨ Exclusive Guest Services
          </div>
        )}
        <p style={{ fontSize: 10, color: t.textSecondary, margin: 0, lineHeight: 1.5 }}>Order food, book a ride, or schedule a photo session — all from your phone.</p>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {SERVICES.map((s, i) => (
            <span key={i} style={{ fontSize: 9, fontWeight: 700, color: s.color, background: t.dark ? s.color + '22' : s.bg, border: '1px solid ' + s.color + '40', borderRadius: 20, padding: '3px 10px' }}>{s.name}</span>
          ))}
        </div>
        <p style={{ fontSize: 7, color: t.footerColor, margin: 0 }}>Powered by RedSpeed</p>
      </div>
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, borderLeft: '1px solid ' + t.divider, flexShrink: 0 }}>
        <QRBlock url={url} qrStyle={qrStyle} theme={t} size={120} />
        <p style={{ fontSize: 8, fontWeight: 800, letterSpacing: 2, color: t.scanCta, margin: 0 }}>SCAN ME</p>
        <p style={{ fontSize: 7, color: t.footerColor, margin: 0 }}>gapgo.redspeed.com.au</p>
      </div>
    </div>
  );
}

/* ---------- Main ---------- */
function QRGenerator() {
  const [hotel, setHotel] = useState(hotels[0]);
  const [startRoom, setStartRoom] = useState('101');
  const [count, setCount] = useState('5');
  const [generated, setGenerated] = useState(false);
  const [printType, setPrintType] = useState('both');
  const [previewRoom, setPreviewRoom] = useState('101');
  const [previewTab, setPreviewTab] = useState('door');
  const [qrStyle, setQrStyle] = useState('classic');
  const [cardStyle, setCardStyle] = useState('white');
  const [bedsideTwoPerPage, setBedsideTwoPerPage] = useState(true);

  const rooms = [];
  if (generated) {
    const start = parseInt(startRoom) || 0;
    const num = Math.min(parseInt(count) || 0, 200);
    for (let i = 0; i < num; i++) rooms.push(String(start + i));
  }

  const previewUrl = BASE_URL + '/' + hotel.id + '/' + previewRoom;

  return (
    <div style={styles.page}>
      <style>{printStyles}</style>

      <div className="no-print" style={styles.controls}>
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div style={styles.redBadge}>REDSPEED</div>
            <div style={styles.headerLine} />
          </div>
          <h1 style={styles.title}>QR Card Generator</h1>
          <p style={styles.sub}>Choose a design, pick a QR style, print on any printer</p>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>SELECT HOTEL</label>
          <div style={styles.hotelGrid}>
            {hotels.map((h) => (
              <button key={h.id} onClick={() => { setHotel(h); setGenerated(false); }}
                style={{ ...styles.hotelBtn, background: hotel.id === h.id ? '#E8001D' : '#111', color: hotel.id === h.id ? '#fff' : '#666', border: hotel.id === h.id ? '1px solid #E8001D' : '1px solid rgba(255,255,255,0.06)' }}>
                {h.name}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>START ROOM</label>
            <input style={styles.input} value={startRoom} onChange={(e) => { setStartRoom(e.target.value); setGenerated(false); }} placeholder="101" />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>NUMBER OF ROOMS</label>
            <input style={styles.input} value={count} onChange={(e) => { setCount(e.target.value); setGenerated(false); }} placeholder="5" />
          </div>
        </div>

        {/* Card design picker */}
        <div style={styles.field}>
          <label style={styles.label}>CARD DESIGN</label>
          <div style={styles.cardStyleGrid}>
            {CARD_STYLES.map((c) => (
              <button key={c.id} onClick={() => setCardStyle(c.id)}
                style={{ ...styles.cardStyleBtn, border: cardStyle === c.id ? '2px solid #E8001D' : '1px solid rgba(255,255,255,0.08)', background: cardStyle === c.id ? '#E8001D12' : '#111' }}>
                <span style={{ ...styles.cardStyleName, color: cardStyle === c.id ? '#fff' : '#999' }}>{c.name}</span>
                <span style={{ ...styles.cardStyleInk, color: c.ink === 'low' ? '#1DB954' : '#FF8A5B' }}>{c.ink === 'low' ? '● Low ink' : '● More ink'}</span>
              </button>
            ))}
          </div>
        </div>

        {/* QR style picker */}
        <div style={styles.field}>
          <label style={styles.label}>QR CODE STYLE</label>
          <div style={styles.qrStyleGrid}>
            {QR_STYLES.map((s) => (
              <button key={s.id} onClick={() => setQrStyle(s.id)}
                style={{ ...styles.qrStyleBtn, border: qrStyle === s.id ? '2px solid #E8001D' : '1px solid rgba(255,255,255,0.08)', background: qrStyle === s.id ? '#E8001D10' : '#111' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, background: '#fff', borderRadius: 8, padding: 6 }}>
                  <StyledQR value="https://gapgo.redspeed.com.au" size={62} styleId={s.id} />
                </div>
                <p style={{ ...styles.qrStyleName, color: qrStyle === s.id ? '#fff' : '#888' }}>{s.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Card type */}
        <div style={styles.field}>
          <label style={styles.label}>CARD TYPE</label>
          <div style={styles.printTypeRow}>
            {[{ id: 'both', label: 'Both', desc: 'Door + Bedside' }, { id: 'door', label: 'Door Only', desc: 'A4 size' }, { id: 'bedside', label: 'Bedside Only', desc: 'A5 size' }].map((t) => (
              <button key={t.id} onClick={() => setPrintType(t.id)}
                style={{ ...styles.typeBtn, background: printType === t.id ? '#E8001D15' : 'transparent', border: printType === t.id ? '1px solid #E8001D' : '1px solid rgba(255,255,255,0.08)', color: printType === t.id ? '#fff' : '#555' }}>
                <span style={styles.typeBtnLabel}>{t.label}</span>
                <span style={styles.typeBtnDesc}>{t.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 2-per-page option, only for bedside */}
        {printType === 'bedside' && (
          <div style={styles.field}>
            <button onClick={() => setBedsideTwoPerPage(!bedsideTwoPerPage)}
              style={{ ...styles.twoUpToggle, border: bedsideTwoPerPage ? '1px solid #1DB954' : '1px solid rgba(255,255,255,0.08)', background: bedsideTwoPerPage ? '#0d1f0d' : '#111' }}>
              <div style={{ ...styles.twoUpDot, background: bedsideTwoPerPage ? '#1DB954' : '#333' }} />
              <div>
                <p style={{ ...styles.twoUpLabel, color: bedsideTwoPerPage ? '#fff' : '#888' }}>2 rooms per page</p>
                <p style={styles.twoUpDesc}>Fit two bedside cards on one A4 sheet — saves paper</p>
              </div>
            </button>
          </div>
        )}

        {/* Preview */}
        <div style={styles.previewBox}>
          <div style={styles.previewTop}>
            <p style={styles.previewTitle}>Live Preview</p>
            <div style={styles.previewRoomRow}>
              <span style={{ fontSize: 11, color: '#555' }}>Room</span>
              <input style={styles.previewRoomInput} value={previewRoom} onChange={(e) => setPreviewRoom(e.target.value)} placeholder="101" />
            </div>
          </div>
          <div style={styles.previewTabs}>
            {[{ id: 'door', label: 'Door Card (A4)' }, { id: 'bedside', label: 'Bedside Card (A5)' }].map((t) => (
              <button key={t.id} onClick={() => setPreviewTab(t.id)}
                style={{ ...styles.previewTab, background: previewTab === t.id ? '#E8001D' : 'transparent', color: previewTab === t.id ? '#fff' : '#555', border: previewTab === t.id ? 'none' : '1px solid rgba(255,255,255,0.08)' }}>
                {t.label}
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            {previewTab === 'door' && (
              <motion.div key={'door' + cardStyle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                <div style={{ transform: 'scale(0.68)', transformOrigin: 'top center', width: 460, marginBottom: -150 }}>
                  <DoorCard hotel={hotel} room={previewRoom} url={previewUrl} qrStyle={qrStyle} cardStyle={cardStyle} />
                </div>
                <p style={styles.previewNote}>Actual print fills full A4</p>
              </motion.div>
            )}
            {previewTab === 'bedside' && (
              <motion.div key={'bedside' + cardStyle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ transform: 'scale(0.8)', transformOrigin: 'top center', width: 500, marginBottom: -30 }}>
                  <BedsideCard hotel={hotel} room={previewRoom} url={previewUrl} qrStyle={qrStyle} cardStyle={cardStyle} />
                </div>
                <p style={styles.previewNote}>Actual print fills half A4 (A5)</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <motion.button whileTap={{ scale: 0.98 }} style={styles.genBtn} onClick={() => setGenerated(true)}>
          Generate {count} Cards
        </motion.button>

        {generated && (
          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} whileTap={{ scale: 0.98 }} style={styles.printBtn} onClick={() => window.print()}>
            Print / Save as PDF
          </motion.button>
        )}

        {generated && (
          <div style={styles.readyBox}>
            <p style={styles.readyTitle}>Ready to print</p>
            <p style={styles.readyText}>{rooms.length} cards · {CARD_STYLES.find((c) => c.id === cardStyle)?.name} · QR: {QR_STYLES.find((s) => s.id === qrStyle)?.name}</p>
          </div>
        )}
      </div>

      {/* PRINT OUTPUT */}
      {generated && printType === 'bedside' && bedsideTwoPerPage
        ? // Bedside cards, 2 per A4 page
          (() => {
            const pairs = [];
            for (let i = 0; i < rooms.length; i += 2) pairs.push(rooms.slice(i, i + 2));
            return pairs.map((pair, idx) => (
              <div key={idx} className="print-only">
                <div className="bedside-pair-page">
                  {pair.map((room, pi) => {
                    const url = BASE_URL + '/' + hotel.id + '/' + room;
                    return (
                      <React.Fragment key={room}>
                        <div className="bedside-pair-item">
                          <div style={{ transform: 'scale(1.1)', transformOrigin: 'center' }}>
                            <BedsideCard hotel={hotel} room={room} url={url} qrStyle={qrStyle} cardStyle={cardStyle} />
                          </div>
                        </div>
                        {pi === 0 && pair.length === 2 && <div className="bedside-cut-line" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ));
          })()
        : // Door cards and/or single-per-page bedside
          generated &&
          rooms.map((room) => {
            const url = BASE_URL + '/' + hotel.id + '/' + room;
            return (
              <div key={room} className="print-only">
                {(printType === 'both' || printType === 'door') && (
                  <div className="door-print-page">
                    <div style={{ transform: 'scale(1.35)', transformOrigin: 'center' }}>
                      <DoorCard hotel={hotel} room={room} url={url} qrStyle={qrStyle} cardStyle={cardStyle} />
                    </div>
                  </div>
                )}
                {(printType === 'both' || printType === 'bedside') && (
                  <div className="bedside-print-page">
                    <div style={{ transform: 'scale(1.1)', transformOrigin: 'center' }}>
                      <BedsideCard hotel={hotel} room={room} url={url} qrStyle={qrStyle} cardStyle={cardStyle} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
    </div>
  );
}

const printStyles = `
  @media screen { .print-only { display: none; } }
  @media print {
    .no-print { display: none !important; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { margin: 0; padding: 0; background: #fff; }
    .print-only { display: block; }
    .door-print-page {
      width: 210mm; min-height: 297mm;
      display: flex; align-items: center; justify-content: center;
      page-break-after: always; break-after: always;
      background: #fff; box-sizing: border-box;
    }
    .bedside-print-page {
      width: 210mm; min-height: 148mm;
      display: flex; align-items: center; justify-content: center;
      page-break-after: always; break-after: always;
      background: #fff; box-sizing: border-box;
    }
    .bedside-pair-page {
      width: 210mm; min-height: 297mm;
      display: flex; flex-direction: column;
      align-items: center; justify-content: space-around;
      page-break-after: always; break-after: always;
      background: #fff; box-sizing: border-box;
      padding: 8mm 0;
    }
    .bedside-pair-item {
      flex: 1;
      display: flex; align-items: center; justify-content: center;
      width: 100%;
    }
    .bedside-cut-line {
      width: 90%;
      border-top: 1px dashed #ccc;
      margin: 0 auto;
    }
  }
`;

const styles = {
  page: { minHeight: '100vh', background: '#0a0a0a', fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' },
  controls: { maxWidth: '540px', margin: '0 auto', padding: '52px 20px 40px' },
  header: { marginBottom: '32px' },
  headerTop: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  redBadge: { background: '#E8001D', color: '#fff', fontSize: '10px', fontWeight: '800', letterSpacing: '2px', padding: '4px 10px', borderRadius: '4px', flexShrink: 0 },
  headerLine: { flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' },
  title: { fontSize: '28px', fontWeight: '700', color: '#fff', margin: '0 0 8px', letterSpacing: '-0.5px' },
  sub: { fontSize: '14px', color: '#555', margin: 0 },
  field: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px', flex: 1 },
  label: { fontSize: '10px', letterSpacing: '2px', color: '#444', fontWeight: '700' },
  hotelGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  hotelBtn: { borderRadius: '10px', padding: '12px 10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease', textAlign: 'left' },
  row: { display: 'flex', gap: '12px' },
  input: { background: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '13px 14px', color: '#fff', fontSize: '16px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  twoUpToggle: { display: 'flex', alignItems: 'center', gap: '12px', borderRadius: '12px', padding: '14px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.2s ease', width: '100%', boxSizing: 'border-box' },
  twoUpDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, transition: 'background 0.2s ease' },
  twoUpLabel: { fontSize: '14px', fontWeight: '600', margin: '0 0 3px' },
  twoUpDesc: { fontSize: '12px', color: '#555', margin: 0 },
  cardStyleGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  cardStyleBtn: { borderRadius: '12px', padding: '14px 12px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '4px' },
  cardStyleName: { fontSize: '14px', fontWeight: '700' },
  cardStyleInk: { fontSize: '10px', fontWeight: '600' },
  qrStyleGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' },
  qrStyleBtn: { borderRadius: '12px', padding: '10px 6px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease', textAlign: 'center' },
  qrStyleName: { fontSize: '10px', fontWeight: '700', margin: 0 },
  printTypeRow: { display: 'flex', gap: '8px' },
  typeBtn: { flex: 1, borderRadius: '10px', padding: '10px 8px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all 0.2s ease' },
  typeBtnLabel: { fontSize: '13px', fontWeight: '700' },
  typeBtnDesc: { fontSize: '10px', color: '#555' },
  previewBox: { background: '#111', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '18px', marginBottom: '20px', overflow: 'hidden' },
  previewTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  previewTitle: { fontSize: '14px', fontWeight: '700', color: '#fff', margin: 0 },
  previewRoomRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  previewRoomInput: { background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 10px', color: '#fff', fontSize: '14px', fontFamily: 'inherit', outline: 'none', width: '70px', textAlign: 'center' },
  previewTabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
  previewTab: { flex: 1, borderRadius: '10px', padding: '9px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease' },
  previewNote: { fontSize: '11px', color: '#444', margin: '8px 0 0', textAlign: 'center' },
  genBtn: { width: '100%', background: '#E8001D', color: '#fff', border: 'none', borderRadius: '12px', padding: '15px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', marginTop: '8px' },
  printBtn: { width: '100%', background: '#fff', color: '#0a0a0a', border: 'none', borderRadius: '12px', padding: '15px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', marginTop: '10px' },
  readyBox: { background: '#0d1f0d', border: '1px solid #1DB95440', borderRadius: '10px', padding: '14px', marginTop: '14px' },
  readyTitle: { fontSize: '12px', fontWeight: '700', color: '#1DB954', margin: '0 0 4px' },
  readyText: { fontSize: '12px', color: '#555', margin: 0 },
};

export default QRGenerator;
